import { useState, useMemo, useEffect } from "react";
import { useDSPGeo } from "../hooks/useDSPGeo";
import DSPTargetSelector from "./DSPTargetSelector";
import { cut, snakeCaseToSentenceCase, toTitleCase, unescapeHTML } from "utils/formatText";
import Tags from "components/core/blocks/Tags";
import { Flex } from "../../../../styled-system/jsx";
import { Label } from "components/custom/programs/fields";
import { useDSPApps } from "../hooks/useDSPApps";
import { css } from "../../../../styled-system/css";
import { useTargetSelector } from "../hooks/useTargetSelector";
import { useDSPContextualProducts } from "../hooks/useDSPContextualProducts";
import ExternalProductLink from "components/custom/merchandise/ExternalProductLink";
import DSPAudienceGroupsForm from "./DSPAudienceGroupsForm";
import CenteredModal from "components/core/basic/CenteredModal";
import { Button, LinkButton } from "components/core/basic/Button";
import { useDSPIabCategories } from "../hooks/useDSPIabCategories";
import { useDSPContextualCategories } from "../hooks/useDSPContextualCategories";
import { useDSPInventoryTargets } from "../hooks/useDSPInventoryTargets";
import { Col, Form, Row } from "react-bootstrap";
import { RadioGroupField } from "./fields";
import { MIN_REQUIRED_INVENTORY_TARGETS } from "./constants";

export const GeoTargetsField = ({ formState, initialFormData, setValue, lineItemId }) => {
  const { setSearch, selectionMode, setSelectionMode, formattedOptions, savedSearchResults, isLoading } =
    useTargetSelector(useDSPGeo, formState, "geoTargetIds", "excludedGeoTargetIds", null, lineItemId);

  const targetSelectionDetails = useMemo(() => {
    return formState?.geoTargetIds
      ?.map((id) => savedSearchResults?.find((result) => result.id === id))
      ?.map((selectedGeoTarget) => {
        const { name, id, category } = selectedGeoTarget ?? {};
        return {
          label: `${name} (${toTitleCase(snakeCaseToSentenceCase(category))})`,
          value: id,
        };
      });
  }, [formState.geoTargetIds, savedSearchResults]);

  const exclusionSelectionDetails = useMemo(() => {
    return formState?.excludedGeoTargetIds
      ?.map((id) => savedSearchResults?.find((result) => result.id === id))
      ?.map((selectedGeoTarget) => {
        const { name, id, category } = selectedGeoTarget ?? {};
        return {
          label: `${name} (${toTitleCase(snakeCaseToSentenceCase(category))})`,
          value: id,
        };
      });
  }, [formState.excludedGeoTargetIds, savedSearchResults]);

  const modeAccessor = selectionMode === "exclude" ? "excludedGeoTargetIds" : "geoTargetIds";

  return (
    <DSPTargetSelector
      lineItemId={lineItemId}
      title={"Location Targets"}
      setSearchQuery={(searchQuery) => {
        setSearch(searchQuery);
      }}
      options={formattedOptions}
      searchResultsLoading={isLoading}
      renderExcludeMode={true}
      selectionMode={selectionMode}
      setSelectionMode={setSelectionMode}
      selectedTargets={targetSelectionDetails}
      selectedExclusions={exclusionSelectionDetails}
      onAdd={(id) => {
        const updatedGeoTargets = [...(formState[modeAccessor] ?? []), id];
        setValue(modeAccessor, updatedGeoTargets, { shouldDirty: true });
      }}
      onRemove={(id, removalType = null) => {
        let accessor = modeAccessor;

        if (removalType === "target") {
          accessor = "geoTargetIds";
        }
        if (removalType === "exclusion") {
          accessor = "excludedGeoTargetIds";
        }

        const updatedGeoTargets = formState[accessor]?.filter((selectedGeoTargetId) => selectedGeoTargetId !== id);
        setValue(accessor, updatedGeoTargets, { shouldDirty: true });
      }}
      onReset={() => {
        setValue("geoTargetIds", initialFormData.geo_target_ids ?? [], { shouldDirty: true });
        setValue("excludedGeoTargetIds", initialFormData.excluded_geo_target_ids ?? [], { shouldDirty: true });
      }}
    />
  );
};

export const AppTargetsField = ({ formState, initialFormData, setValue, lineItemId }) => {
  const { setSearch, selectionMode, setSelectionMode, formattedOptions, savedSearchResults, isLoading } =
    useTargetSelector(
      useDSPApps,
      formState,
      "appTargetIds",
      "excludedAppTargetIds",
      (v) => {
        return `${v.name} (${v.id})`;
      },
      lineItemId,
    );

  const targetSelectionDetails = useMemo(() => {
    return formState?.appTargetIds
      ?.map((id) => savedSearchResults?.find((result) => result.id === id))
      ?.map((selectedAppTarget) => {
        const { name, id } = selectedAppTarget ?? {};
        return {
          label: `${name} (${id})`,
          value: id,
        };
      });
  }, [formState.appTargetIds, savedSearchResults]);

  const exclusionSelectionDetails = useMemo(() => {
    return formState?.excludedAppTargetIds
      ?.map((id) => savedSearchResults?.find((result) => result.id === id))
      ?.map((selectedAppTarget) => {
        const { name, id } = selectedAppTarget ?? {};
        return {
          label: `${name} (${id})`,
          value: id,
        };
      });
  }, [formState.excludedAppTargetIds, savedSearchResults]);

  const modeAccessor = selectionMode === "exclude" ? "excludedAppTargetIds" : "appTargetIds";

  return (
    <DSPTargetSelector
      lineItemId={lineItemId}
      title={"App Targets"}
      setSearchQuery={(searchQuery) => {
        setSearch(searchQuery);
      }}
      options={formattedOptions}
      searchResultsLoading={isLoading}
      renderExcludeMode={true}
      selectionMode={selectionMode}
      setSelectionMode={setSelectionMode}
      selectedTargets={targetSelectionDetails}
      selectedExclusions={exclusionSelectionDetails}
      onAdd={(id) => {
        const updatedAppTargets = [...(formState[modeAccessor] ?? []), id];
        setValue(modeAccessor, updatedAppTargets, { shouldDirty: true });
      }}
      onRemove={(id, removalType = null) => {
        let accessor = modeAccessor;
        if (removalType === "target") {
          accessor = "appTargetIds";
        }
        if (removalType === "exclusion") {
          accessor = "excludedAppTargetIds";
        }

        const updatedAppTargets = formState[accessor]?.filter((selectedGeoTargetId) => selectedGeoTargetId !== id);
        setValue(accessor, updatedAppTargets, { shouldDirty: true });
      }}
      onReset={() => {
        setValue("appTargetIds", initialFormData.app_target_ids ?? [], { shouldDirty: true });
        setValue("excludedAppTargetIds", initialFormData.excluded_app_target_ids ?? [], { shouldDirty: true });
      }}
    />
  );
};

export const DomainTargetsField = ({ formState, initialFormData, setValue, lineItemId }) => {
  const [error, setError] = useState(null);
  const { selectionMode, setSelectionMode, formattedOptions, isLoading } = useTargetSelector(
    useDSPApps,
    formState,
    "targetDomains",
    "excludedTargetDomains",
  );

  const MIN_REQUIRED_DOMAINS = 50;
  const modeAccessor = selectionMode === "exclude" ? "excludedDomainListTargetIds" : "domainListTargetIds";

  const validateDomainUrl = (url) => {
    const validDomainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]{1,63}\.)+[a-zA-Z]{2,}$/;
    return validDomainRegex.test(url);
  };

  if (formState.targetDomains?.length && formState.targetDomains?.length < MIN_REQUIRED_DOMAINS && !error) {
    setError(
      `At least 50 domain targets are required. Please add ${MIN_REQUIRED_DOMAINS - formState.targetDomains?.length} more domain${MIN_REQUIRED_DOMAINS - formState.targetDomains?.length > 1 ? "s" : ""}.`,
    );
    return;
  }

  return (
    <DSPTargetSelector
      lineItemId={lineItemId}
      title={"Domain Targets"}
      options={formattedOptions}
      hideSearchField={true}
      searchResultsLoading={isLoading}
      renderExcludeMode={false}
      renderTargetMode={false}
      customEmptyResultsMessage="No domain lists were found. You can manually add domain targets or exclusions in the 'Enter Your Own' section, or you can create domain lists on the Amazon DSP console and they will show up here."
      selectionMode={"custom"}
      setSelectionMode={setSelectionMode}
      selectedTargets={formState.targetDomains?.map((domain) => {
        return {
          label: domain,
          value: domain,
        };
      })}
      onAdd={(id) => {
        const updatedDomainTargets = [...(formState[modeAccessor] ?? []), id];
        setValue(modeAccessor, updatedDomainTargets, { shouldDirty: true });
      }}
      onRemove={(id, removalType = null) => {
        let accessor = modeAccessor;

        if (removalType === "target") {
          accessor = typeof id === "string" ? "targetDomains" : "domainTargetIds";
        }
        if (removalType === "exclusion") {
          accessor = typeof id === "string" ? "excludedTargetDomains" : "excludedDomainTargetIds";
        }
        const updatedDomainTargets = formState[accessor]?.filter(
          (selectedDomainTargetId) => selectedDomainTargetId !== id,
        );
        setValue(accessor, updatedDomainTargets);
      }}
      onReset={() => {
        setValue("targetDomains", initialFormData.targetDomains ?? [], { shouldDirty: true });
        setValue("excludedTargetDomains", initialFormData.excludedTargetDomains ?? [], { shouldDirty: true });
        setValue("domainTargetIds", initialFormData.domainTargetIds ?? [], { shouldDirty: true });
        setValue("excludedDomainTargetIds", initialFormData.excludedDomainTargetIds ?? [], { shouldDirty: true });
      }}
      additionalTabs={[
        {
          id: "custom",
          label: "Enter Your Own",
          content: (
            <Flex direction={"column"} className="p-3 mt-4" gap="large">
              <p className="fs-4">Enter domains to target and exclude using the input below.</p>
              <div className={css({ overflowY: "auto" })}>
                <Tags
                  saveFilledTags={(targetDomains) => {
                    if (!targetDomains?.every((domain) => validateDomainUrl(domain))) {
                      setError("Invalid domain provided.");
                      return;
                    }
                    if (error) {
                      setError("");
                    }

                    setValue("targetDomains", targetDomains, { shouldDirty: true });
                  }}
                  filledTags={formState.targetDomains}
                  name={
                    <Label>
                      <Flex className={css({ width: "100%" })} gap={"large"} justifyContent={"space-between"}>
                        Target Domains{" "}
                        <LinkButton
                          onClick={() => setValue("targetDomains", [], { shouldDirty: true })}
                          className={css({ marginStart: "auto" })}
                        >
                          Remove All
                        </LinkButton>
                      </Flex>
                    </Label>
                  }
                  placeholder="Enter domains..."
                />
              </div>
              {error ?
                <small className="text-danger">{error}</small>
              : null}
              <div className={css({ overflowY: "auto" })}>
                <Tags
                  saveFilledTags={(targetDomains) => {
                    if (!targetDomains?.every((domain) => validateDomainUrl(domain))) {
                      setError("Invalid domain provided. Please enter a valid domain.");
                      return;
                    }

                    if (error) {
                      setError("");
                    }

                    setValue("excludedTargetDomains", targetDomains, { shouldDirty: true });
                  }}
                  filledTags={formState.excludedTargetDomains}
                  name={
                    <Label>
                      <Flex className={css({ width: "100%" })} justifyContent={"space-between"} gap={"large"}>
                        Excluded Target Domains
                        <LinkButton
                          onClick={() => setValue("excludedTargetDomains", [], { shouldDirty: true })}
                          className={css({ marginStart: "auto" })}
                        >
                          Remove All
                        </LinkButton>
                      </Flex>
                    </Label>
                  }
                  hideEditButton={false}
                  placeholder="Enter domains..."
                />
              </div>
            </Flex>
          ),
        },
      ]}
    />
  );
};

export const ContextualProductTargetsField = ({ formState, initialFormData, setValue, lineItemId }) => {
  const { setSearch, selectionMode, setSelectionMode, formattedOptions, savedSearchResults, isLoading } =
    useTargetSelector(
      useDSPContextualProducts,
      formState,
      "contextualProductTargetIds",
      "excludedContextualProductTargetIds",
      (v) => {
        return `${v.id}: ${v.name}`;
      },
      lineItemId,
    );

  const targetSelectionDetails = useMemo(() => {
    return formState?.contextualProductTargetIds
      ?.map((asin) => savedSearchResults?.find((result) => result?.asin === asin))
      ?.map((selectedProductTarget) => {
        const { title, asin } = selectedProductTarget ?? {};
        return {
          label: (
            <span>
              {asin}: {cut(title, 80)}
              <ExternalProductLink asin={asin} />
            </span>
          ),
          value: asin,
        };
      });
  }, [formState.contextualProductTargetIds, savedSearchResults]);

  const exclusionSelectionDetails = useMemo(() => {
    return formState?.excludedContextualProductTargetIds
      ?.map((asin) => savedSearchResults?.find((result) => result?.asin === asin))
      ?.map((selectedProductTarget) => {
        const { title, asin } = selectedProductTarget ?? {};
        return {
          label: (
            <span>
              {asin}: {cut(title, 80)}
              <ExternalProductLink asin={asin} />
            </span>
          ),
          value: asin,
        };
      });
  }, [formState.excludedContextualProductTargetIds, savedSearchResults]);

  const modeAccessor =
    selectionMode === "exclude" ? "excludedContextualProductTargetIds" : "contextualProductTargetIds";

  return (
    <DSPTargetSelector
      lineItemId={lineItemId}
      title={"Contextual Product Targets"}
      altButtonText={"Select New Product Targets"}
      setSearchQuery={(searchQuery) => {
        setSearch(searchQuery);
      }}
      pageSize={8}
      options={formattedOptions}
      searchResultsLoading={isLoading}
      renderExcludeMode={true}
      selectionMode={selectionMode}
      setSelectionMode={setSelectionMode}
      selectedTargets={targetSelectionDetails}
      selectedExclusions={exclusionSelectionDetails}
      onAdd={(id) => {
        const updatedAppTargets = [...(formState[modeAccessor] ?? []), id];
        setValue(modeAccessor, updatedAppTargets, { shouldDirty: true });
      }}
      onRemove={(id, removalType = null) => {
        let accessor = modeAccessor;
        if (removalType === "target") {
          accessor = "contextualProductTargetIds";
        }
        if (removalType === "exclusion") {
          accessor = "excludedContextualProductTargetIds";
        }

        const updatedAppTargets = formState[accessor]?.filter(
          (selectedProductTargetId) => selectedProductTargetId !== id,
        );
        setValue(accessor, updatedAppTargets, { shouldDirty: true });
      }}
      onReset={() => {
        setValue("contextualProductTargetIds", initialFormData.contextual_product_target_ids ?? [], {
          shouldDirty: true,
        });
        setValue("excludedContextualProductTargetIds", initialFormData.excluded_contextual_product_target_ids ?? [], {
          shouldDirty: true,
        });
      }}
      customLabelFormatter={(product) => {
        const { title, asin, imageUrl } = product;

        return (
          <Flex>
            <div
              style={{
                userSelect: "none",
                WebkitUserSelect: "none",
                flexShrink: 0,
                width: 50,
                height: 50,
                marginRight: "10px",
                background: `url(${imageUrl}) no-repeat center / contain`,
              }}
              draggable="false"
            />
            <div>
              <span className={css({ fontWeight: 500, overflow: "ellipsis", lineClamp: 2 })}>{title}</span>
              <span className={css({ fontWeight: 500, fontSize: "smaller" })}>
                {asin} <ExternalProductLink asin={asin} />
              </span>
            </div>
          </Flex>
        );
      }}
    />
  );
};

export const ContextualCategoryTargetsField = ({ formState, initialFormData, setValue, lineItemId }) => {
  const { setSearch, selectionMode, setSelectionMode, formattedOptions, savedSearchResults, isLoading } =
    useTargetSelector(
      useDSPContextualCategories,
      formState,
      "contextualCategoryTargetIds",
      "excludedContextualCategoryTargetIds",
      (v) => {
        return `${v.name} (in ${v.path})`;
      },
      lineItemId,
    );

  const targetSelectionDetails = useMemo(() => {
    return formState?.contextualCategoryTargetIds
      ?.map((id) => savedSearchResults?.find((result) => result.browseNodeId === id))
      ?.map((selectedCategoryTarget) => {
        const { categoryName, browseNodeId } = selectedCategoryTarget ?? {};

        return {
          label: categoryName,
          value: browseNodeId,
        };
      });
  }, [formState.contextualCategoryTargetIds, savedSearchResults]);

  const exclusionSelectionDetails = useMemo(() => {
    return formState?.excludedContextualCategoryTargetIds
      ?.map((id) => savedSearchResults?.find((result) => result.browseNodeId === id))
      ?.map((selectedCategoryTarget) => {
        const { categoryName, browseNodeId } = selectedCategoryTarget ?? {};
        return {
          label: categoryName,
          value: browseNodeId,
        };
      });
  }, [formState.excludedContextualCategoryTargetIds, savedSearchResults]);

  const modeAccessor =
    selectionMode === "exclude" ? "excludedContextualCategoryTargetIds" : "contextualCategoryTargetIds";

  return (
    <DSPTargetSelector
      lineItemId={lineItemId}
      title={"Contextual Category Targets"}
      altButtonText={"Select New Product Category Targets"}
      setSearchQuery={(searchQuery) => {
        setSearch(searchQuery);
      }}
      pageSize={10}
      options={formattedOptions}
      searchResultsLoading={isLoading}
      renderExcludeMode={true}
      selectionMode={selectionMode}
      setSelectionMode={setSelectionMode}
      selectedTargets={targetSelectionDetails}
      selectedExclusions={exclusionSelectionDetails}
      onAdd={(id) => {
        const updatedAppTargets = [...(formState[modeAccessor] ?? []), id];
        setValue(modeAccessor, updatedAppTargets, { shouldDirty: true });
      }}
      onRemove={(id, removalType = null) => {
        let accessor = modeAccessor;
        if (removalType === "target") {
          accessor = "contextualCategoryTargetIds";
        }
        if (removalType === "exclusion") {
          accessor = "excludedContextualCategoryTargetIds";
        }

        const updatedAppTargets = formState[accessor]?.filter(
          (selectedProductTargetId) => selectedProductTargetId !== id,
        );
        setValue(accessor, updatedAppTargets, { shouldDirty: true });
      }}
      onReset={() => {
        setValue("contextualCategoryTargetIds", initialFormData.contextual_category_target_ids ?? [], {
          shouldDirty: true,
        });
        setValue("excludedContextualCategoryTargetIds", initialFormData.excluded_contextual_category_target_ids ?? [], {
          shouldDirty: true,
        });
      }}
    />
  );
};

export const ContextualKeywordTargetsField = ({ formState, setValue, lineItemId }) => {
  const { setSearch, formattedOptions, isLoading } = useTargetSelector(
    useDSPContextualProducts,
    formState,
    "contextualKeywords",
    "excludedContextualKeywords",
    (v) => {
      return `${v.id}: ${v.name}`;
    },
    lineItemId,
  );
  return (
    <DSPTargetSelector
      lineItemId={lineItemId}
      title={"Contextual Keyword Targets"}
      altButtonText={"Select New Keyword Targets"}
      setSearchQuery={(searchQuery) => {
        setSearch(searchQuery);
      }}
      pageSize={8}
      options={formattedOptions}
      searchResultsLoading={isLoading}
      renderTargetMode={false}
      renderExcludeMode={false}
      selectionMode={"custom"}
      selectedTargets={formState.contextualKeywords?.map((keyword) => {
        return {
          label: keyword,
          value: keyword,
        };
      })}
      onRemove={(keyword) => {
        const accessor = "contextualKeywords";

        const updatedContextualKeywords = formState[accessor]?.filter((selectedKeyword) => selectedKeyword !== keyword);
        setValue(accessor, updatedContextualKeywords, { shouldDirty: true });
      }}
      additionalTabs={[
        {
          id: "custom",
          label: "Enter Your Own",
          content: (
            <Flex direction={"column"} className="p-3 mt-4" gap="large">
              <p className="fs-4">Enter keywords to target using the input below.</p>
              <Tags
                saveFilledTags={(keywords) => {
                  setValue("contextualKeywords", keywords, { shouldDirty: true });
                }}
                filledTags={formState.contextualKeywords}
                name={<Label>Target Contextual Keywords</Label>}
                hideEditButton={true}
                placeholder="Enter keywords..."
              />
            </Flex>
          ),
        },
      ]}
    />
  );
};

export const AudienceTargetingField = ({ entityId, createMode, setValue, formState }) => {
  const [showSelector, setShowSelector] = useState(false);

  return (
    <Flex direction={"column"}>
      <Flex gap="large" alignItems={"center"} justifyContent={"space-between"}>
        <Label className={css({ paddingTop: "10px" })}>Audience Groups</Label>
        <div>
          <LinkButton onClick={() => setShowSelector(true)}>Modify</LinkButton>
        </div>
      </Flex>

      <p className={css({ fontSize: "14px" })}>
        {formState.audienceGroupTargets?.length ?? 0} Selected Target
        {formState.audienceGroupTargets?.length === 1 ? "" : "s"}
      </p>
      <p className={css({ fontSize: "14px" })}>
        {formState.audienceGroupExclusions?.length ?? 0} Selected Exclusion
        {formState.audienceGroupExclusions?.length === 1 ? "" : "s"}
      </p>
      <CenteredModal
        show={showSelector}
        closeModal={() => setShowSelector(false)}
        title={`Update Line Item Audience Groups`}
        adPlanModal={true}
      >
        <div className={css({ height: "750px", padding: "50px" })}>
          <DSPAudienceGroupsForm
            lineItemId={createMode ? null : entityId}
            lineItemForm={true}
            onCancel={() => {
              setShowSelector(false);
            }}
            onSave={(audienceData) => {
              setValue(
                "audienceGroupTargets",
                audienceData
                  ?.filter((audience) => !audience.negative)
                  ?.map((audience) => audience.target_details.audienceTarget),
                { shouldDirty: true },
              );
              setValue(
                "audienceGroupExclusions",
                audienceData
                  ?.filter((audience) => audience.negative)
                  ?.map((audience) => audience.target_details.audienceTarget),
                { shouldDirty: true },
              );
              setShowSelector(false);
            }}
          />
        </div>
      </CenteredModal>
    </Flex>
  );
};

export const IabCategoriesField = ({ formState, lineItemId, setValue, initialFormData }) => {
  const [search, setSearch] = useState("");

  const { selectionMode, setSelectionMode, formattedOptions, savedSearchResults, isLoading } = useTargetSelector(
    useDSPIabCategories,
    formState,
    "iabCategoryTargetIds",
    "excludedIabCategoryTargetIds",
    (v) => {
      return unescapeHTML(`${v.name} ${v.parentDisplayName ? "(in " + v.parentDisplayName + ")" : ""}`);
    },
    lineItemId,
    {
      inventoryType: "STANDARD_DISPLAY",
    },
  );

  const targetSelectionDetails = useMemo(() => {
    return formState?.iabCategoryTargetIds
      ?.map((id) => savedSearchResults?.find((result) => result.id === id))
      ?.map((selectedCategoryTarget) => {
        const { name, id, parentDisplayName } = selectedCategoryTarget ?? {};
        return {
          label: unescapeHTML(`${name} ${parentDisplayName ? "(in " + parentDisplayName + ")" : ""}`),
          value: id,
        };
      });
  }, [formState.iabCategoryTargetIds, savedSearchResults]);

  const modeAccessor = "iabCategoryTargetIds";

  const filteredOptions = useMemo(() => {
    return formattedOptions?.filter((option) => option.label.toLowerCase()?.includes(search.toLowerCase()));
  }, [search, formattedOptions]);

  return (
    <DSPTargetSelector
      lineItemId={lineItemId}
      staticData={true}
      title={"IAB Category Targets"}
      setSearchQuery={(searchQuery) => {
        setSearch(searchQuery);
      }}
      pageSize={20}
      options={filteredOptions}
      searchResultsLoading={isLoading}
      renderExcludeMode={false}
      selectionMode={selectionMode}
      setSelectionMode={setSelectionMode}
      selectedTargets={targetSelectionDetails}
      selectedExclusions={[]}
      onAdd={(id) => {
        const updatedIabCategoryTargets = [...(formState[modeAccessor] ?? []), id];
        setValue(modeAccessor, updatedIabCategoryTargets, { shouldDirty: true });
      }}
      onRemove={(id) => {
        const accessor = "iabCategoryTargetIds";

        const updatedIabCategoryTargets = formState[accessor]?.filter(
          (selectedIabCategoryTargetId) => selectedIabCategoryTargetId !== id,
        );
        setValue(accessor, updatedIabCategoryTargets, { shouldDirty: true });
      }}
      onReset={() => {
        setValue("iabCategoryTargetIds", initialFormData.iab_category_target_ids ?? [], { shouldDirty: true });
      }}
    />
  );
};

export const AmazonInventoriesField = ({ lineItemId, formState, setValue, control, combinedInventoryCount }) => {
  const { data: amazonInventoryOptions } = useDSPInventoryTargets(
    formState.inventoryType,
    "AMAZON",
    formState.mediaDevices,
  );

  // By default, assign all inventory source options when refetched on device target changes
  useEffect(() => {
    if (!lineItemId) {
      setValue(
        "amazonInventories",
        amazonInventoryOptions?.map((o) => o.value),
        {
          shouldDirty: true,
        },
      );
    }
  }, [amazonInventoryOptions, setValue, lineItemId]);

  return (
    <Row className="py-3">
      <Col xs={12} lg={11} className={css({ maxWidth: "100%", overflowX: "auto" })}>
        <Label>Amazon Inventories</Label>
        <Button
          className={css({ marginBottom: "medium" })}
          onClick={() => {
            if (formState.amazonInventories?.length !== amazonInventoryOptions?.length) {
              setValue(
                "amazonInventories",
                amazonInventoryOptions?.map((o) => o.value),
                {
                  shouldDirty: true,
                },
              );
            } else {
              setValue("amazonInventories", [], {
                shouldDirty: true,
              });
            }
          }}
        >
          {formState.amazonInventories?.length === amazonInventoryOptions?.length ? "Clear All" : "Select All"}
        </Button>
        <RadioGroupField
          options={amazonInventoryOptions}
          fieldName={"amazonInventories"}
          stacked={true}
          control={control}
          deriveFieldValue={(value) => {
            return formState.amazonInventories?.includes(value);
          }}
          onChange={(value, checked) => {
            let updatedInventories = formState.amazonInventories ?? [];

            if (checked) {
              updatedInventories.push(value);
            } else {
              updatedInventories = updatedInventories.filter((inventory) => inventory !== value);
            }

            setValue("amazonInventories", updatedInventories, { shouldDirty: true });
          }}
          disableDeselection={combinedInventoryCount <= MIN_REQUIRED_INVENTORY_TARGETS}
        />
      </Col>
    </Row>
  );
};

export const ThirdPartyInventoriesField = ({ lineItemId, formState, setValue, control, combinedInventoryCount }) => {
  const [showSelector, setShowSelector] = useState(false);

  const { data: thirdPartyInventoryOptions } = useDSPInventoryTargets(
    formState.inventoryType,
    "THIRD_PARTY_EXCHANGE",
    formState.mediaDevices,
  );

  // By default, assign all inventory source options when refetched on device target changes
  useEffect(() => {
    if (!lineItemId) {
      setValue(
        "thirdPartyInventories",
        thirdPartyInventoryOptions?.map((o) => o.value),
        {
          shouldDirty: true,
        },
      );
    }
  }, [thirdPartyInventoryOptions, setValue, lineItemId]);

  const handleChange = (value, checked) => {
    let updatedInventories = formState.thirdPartyInventories ?? [];

    if (checked) {
      updatedInventories.push(value);
    } else {
      updatedInventories = updatedInventories.filter((inventory) => inventory !== value);
    }

    setValue("thirdPartyInventories", updatedInventories, { shouldDirty: true });
  };

  return (
    <Form.Group>
      <Flex gap="large" alignItems={"center"} justifyContent={"space-between"}>
        <Label className={css({ paddingTop: "10px" })}>Third Party Inventories</Label>
        <div>
          <LinkButton
            onClick={() => {
              setShowSelector(!showSelector);
            }}
          >
            {formState.thirdPartyInventories?.length ? "Modify" : "Add Targets"}
          </LinkButton>
        </div>
      </Flex>
      <Button
        className={css({ marginBottom: "medium" })}
        onClick={() => {
          if (formState.thirdPartyInventories?.length !== thirdPartyInventoryOptions?.length) {
            setValue(
              "thirdPartyInventories",
              thirdPartyInventoryOptions?.map((o) => o.value),
              {
                shouldDirty: true,
              },
            );
          } else {
            setValue("thirdPartyInventories", [], {
              shouldDirty: true,
            });
          }
        }}
      >
        {formState.thirdPartyInventories?.length === thirdPartyInventoryOptions?.length ? "Clear All" : "Select All"}
      </Button>
      <Flex direction="column" className="mb-4">
        <p className={css({ fontSize: "14px" })}>
          {formState.thirdPartyInventories?.length} Selected{" "}
          {formState.thirdPartyInventories?.length === 1 ? "Inventory" : "Inventories"}
        </p>
        <RadioGroupField
          options={thirdPartyInventoryOptions
            ?.filter((o) => formState.thirdPartyInventories?.includes(o.value))
            ?.slice(0, 3)}
          fieldName={"thirdPartyInventories"}
          stacked={true}
          control={control}
          deriveFieldValue={(value) => {
            return formState.thirdPartyInventories?.includes(value);
          }}
          onChange={handleChange}
          disableDeselection={combinedInventoryCount <= MIN_REQUIRED_INVENTORY_TARGETS}
        />
        {formState.thirdPartyInventories?.length > 3 ?
          <small className={css({ paddingStart: "36px" })}>+ {formState.thirdPartyInventories?.length - 3} more</small>
        : null}
      </Flex>

      <CenteredModal
        show={showSelector}
        closeModal={() => setShowSelector(false)}
        title={`Update Third Party Inventories`}
        adPlanModal={true}
      >
        <div className="dsp-target-selector">
          <Row className={css({ margin: "0 50px", height: "690px" })}>
            <Col xs={12} lg={6} className={css({ maxWidth: "100%", overflowX: "auto" })}>
              <RadioGroupField
                options={thirdPartyInventoryOptions?.slice(0, thirdPartyInventoryOptions?.length / 2)}
                fieldName={"thirdPartyInventories"}
                stacked={true}
                control={control}
                deriveFieldValue={(value) => {
                  return formState.thirdPartyInventories?.includes(value);
                }}
                onChange={handleChange}
              />
            </Col>
            <Col xs={12} lg={6} className={css({ maxWidth: "100%", overflowX: "auto" })}>
              <RadioGroupField
                options={thirdPartyInventoryOptions?.slice(thirdPartyInventoryOptions?.length / 2)}
                fieldName={"thirdPartyInventories"}
                stacked={true}
                control={control}
                deriveFieldValue={(value) => {
                  return formState.thirdPartyInventories?.includes(value);
                }}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Flex justifyContent={"center"} gap={"large"} className={"pb-5"}>
            <Button
              variant="secondary"
              onPress={() => {
                setShowSelector(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onPress={() => {
                setShowSelector(false);
              }}
            >
              Confirm
            </Button>
          </Flex>
        </div>
      </CenteredModal>
    </Form.Group>
  );
};

export const AmazonPublisherDirectInventoriesField = ({
  lineItemId,
  formState,
  setValue,
  control,
  combinedInventoryCount,
}) => {
  const { data: apdInventoryOptions } = useDSPInventoryTargets(
    formState.inventoryType,
    "AMAZON_PUBLISHER_DIRECT",
    formState.mediaDevices,
  );

  // By default, assign all inventory source options when refetched on device target changes
  useEffect(() => {
    if (!lineItemId) {
      setValue(
        "amazonPublisherDirectInventories",
        apdInventoryOptions?.map((o) => o.value),
        {
          shouldDirty: true,
        },
      );
    }
  }, [apdInventoryOptions, setValue, lineItemId]);

  return (
    <Row className="py-3">
      <Col xs={12} lg={11} className={css({ maxWidth: "100%", overflowX: "auto" })}>
        <RadioGroupField
          label={"Amazon Publisher Direct"}
          options={apdInventoryOptions}
          fieldName={"amazonPublisherDirectInventories"}
          stacked={true}
          control={control}
          deriveFieldValue={(value) => {
            return formState.amazonPublisherDirectInventories?.includes(value);
          }}
          onChange={(value, checked) => {
            let updatedInventories = formState.amazonPublisherDirectInventories ?? [];

            if (checked) {
              updatedInventories.push(value);
            } else {
              updatedInventories = updatedInventories.filter((inventory) => inventory !== value);
            }

            setValue("amazonPublisherDirectInventories", updatedInventories, { shouldDirty: true });
          }}
          disableDeselection={combinedInventoryCount <= MIN_REQUIRED_INVENTORY_TARGETS}
        />
      </Col>
    </Row>
  );
};
